import { FC, useState, useCallback } from "react"
import { LoanOfficer } from "../../../types"
import { useCreateRateflowPriceMutation } from "../../../redux/queries"
import { PrimaryBtn } from "../buttons"
import Select from "../form/select"
import { Play, CheckCircle, XCircle, Loader2 } from "lucide-react"

interface Props {
  rateflowId: string
  members: LoanOfficer[]
}

// Basic loan scenario for testing connectivity (bb_request format)
const TEST_SCENARIO = {
  loan_amount: 400000,
  list_price: 500000,
  credit_score: 740,
  loan_purpose: "purchase",
  loan_type: "Conventional",
  loan_term: 30,
  property_type: "single_family_home",
  residency_type: "primary_home",
  state: "CA",
  county: "Los Angeles",
  zipcode: "90210"
}

type TestStatus = "idle" | "running" | "success" | "error"

const ConnectivityTest: FC<Props> = ({ rateflowId, members }) => {
  const [selectedLoid, setSelectedLoid] = useState<string>("")
  const [testStatus, setTestStatus] = useState<TestStatus>("idle")
  const [testResult, setTestResult] = useState<any>(null)
  const [errorMessage, setErrorMessage] = useState<string>("")

  const [createPrice, { isLoading }] = useCreateRateflowPriceMutation()

  const memberOptions = [
    { id: "", name: "-- Use default (no LO) --" },
    ...members.map(m => ({
      id: m.loid || m.uid || "",
      name: `${m.first_name} ${m.last_name} (${m.email})`
    }))
  ]

  const handleRunTest = useCallback(async () => {
    setTestStatus("running")
    setTestResult(null)
    setErrorMessage("")

    try {
      const payload: any = {
        id: rateflowId,
        ...TEST_SCENARIO
      }

      // Add loid if an LO is selected
      if (selectedLoid) {
        payload.loid = selectedLoid
      }

      const result = await createPrice(payload).unwrap()

      // Check if we got valid results
      if (Array.isArray(result) && result.length > 0) {
        setTestStatus("success")
        setTestResult({
          rateCount: result.length,
          sampleRate: result[0]
        })
      } else if ((result as any)?.status === "error") {
        setTestStatus("error")
        setErrorMessage(
          (result as any)?.message || "Pricing engine returned an error"
        )
      } else {
        setTestStatus("success")
        setTestResult({
          rateCount: 0,
          message: "No rates returned for this scenario"
        })
      }
    } catch (error: any) {
      setTestStatus("error")
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          "Failed to connect to pricing engine"
      )
    }
  }, [rateflowId, selectedLoid, createPrice])

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-600 mb-4">
          Test connectivity to the pricing engine using a basic loan scenario.
          Select a loan officer to test their specific configuration, or leave
          empty to use the default settings.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Select
              label="Loan Officer (optional)"
              name="test-lo"
              data={memberOptions}
              selected={selectedLoid}
              setSelected={(value: string) => setSelectedLoid(value)}
            />
          </div>

          <div className="flex items-end">
            <PrimaryBtn
              type="button"
              onClick={handleRunTest}
              disabled={isLoading}
              className="flex items-center"
            >
              {isLoading ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Play size={16} className="mr-2" />
              )}
              {isLoading ? "Testing..." : "Run Test"}
            </PrimaryBtn>
          </div>
        </div>

        {/* Test Scenario Display */}
        <div className="bg-white rounded border border-gray-200 p-3 mb-4">
          <h5 className="text-sm font-medium mb-2">Test Scenario</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
            <div>
              <span className="text-gray-400">Loan Amount:</span> $
              {TEST_SCENARIO.loan_amount.toLocaleString()}
            </div>
            <div>
              <span className="text-gray-400">List Price:</span> $
              {TEST_SCENARIO.list_price.toLocaleString()}
            </div>
            <div>
              <span className="text-gray-400">Credit Score:</span>{" "}
              {TEST_SCENARIO.credit_score}
            </div>
            <div>
              <span className="text-gray-400">Loan Type:</span>{" "}
              {TEST_SCENARIO.loan_type}
            </div>
            <div>
              <span className="text-gray-400">Purpose:</span>{" "}
              {TEST_SCENARIO.loan_purpose}
            </div>
            <div>
              <span className="text-gray-400">Term:</span>{" "}
              {TEST_SCENARIO.loan_term} years
            </div>
            <div>
              <span className="text-gray-400">Property:</span>{" "}
              {TEST_SCENARIO.property_type}
            </div>
            <div>
              <span className="text-gray-400">LTV:</span>{" "}
              {(
                (TEST_SCENARIO.loan_amount / TEST_SCENARIO.list_price) *
                100
              ).toFixed(0)}
              %
            </div>
          </div>
        </div>

        {/* Test Results */}
        {testStatus === "success" && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5" />
              <div>
                <h5 className="font-medium text-green-800">
                  Connection Successful
                </h5>
                <p className="text-sm text-green-700 mt-1">
                  {testResult?.rateCount > 0
                    ? `Received ${testResult.rateCount} rate(s) from the pricing engine.`
                    : testResult?.message ||
                      "Pricing engine responded successfully."}
                </p>
                {testResult?.sampleRate && (
                  <div className="mt-2 text-xs text-green-600">
                    <span className="font-medium">Sample Rate:</span>{" "}
                    {testResult.sampleRate.rate}% @{" "}
                    {testResult.sampleRate.price}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {testStatus === "error" && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <XCircle size={20} className="text-red-600 mt-0.5" />
              <div>
                <h5 className="font-medium text-red-800">Connection Failed</h5>
                <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConnectivityTest
